import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  TIME_BUDGET_CHOICES,
  type TimeBudget,
  type TimeBudgetChoice,
  budgetForChoice,
  choiceForBudget,
  normalizeTimeBudget,
} from '@goaltree/backend/convex/lib/timeBudget'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import type { TFunction } from 'i18next'
import { ChevronDown, Clock, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { markReplanPending, patchGoal } from '@/lib/goals-optimistic'
import { handleUsageLimit } from '@/lib/usage-limit'

export function timeBudgetLabel(t: TFunction, budget?: TimeBudget | null) {
  const choice = choiceForBudget(budget)
  if (choice) return t(`timeBudget.choice.${choice}`)
  return t('timeBudget.custom', { hours: Math.round(((budget?.minutesPerWeek ?? 0) / 60) * 2) / 2 })
}

// Goal sheet "Time per week": change the budget, then optionally rebalance the
// undone tasks to it (a metered replan; finished tasks are never touched).
export function GoalTimeBudget({
  goalId,
  budget,
  replanPending,
  gate,
}: {
  goalId: Id<'nodes'>
  budget?: TimeBudget
  replanPending?: boolean
  gate: (fn: () => void) => void
}) {
  const { t } = useTranslation()
  const save = useMutation(api.nodes.setGoalTimeBudget).withOptimisticUpdate(
    (store, { goalId, timeBudget }) =>
      patchGoal(store, goalId, { timeBudget: normalizeTimeBudget(timeBudget) }),
  )
  const replan = useMutation(api.replanQueue.triggerReplan).withOptimisticUpdate(
    (store, { goalId }) => markReplanPending(store, goalId),
  )
  const [local, setLocal] = useState<TimeBudget | null>(budget ?? null)
  const [open, setOpen] = useState(false)
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    setLocal(budget ?? null)
    setChanged(false)
    setOpen(false)
  }, [goalId])

  function pick(choice: TimeBudgetChoice) {
    const next = budgetForChoice(choice) ?? null
    const prev = local
    setOpen(false)
    if (choiceForBudget(prev) === choice) return
    setLocal(next)
    setChanged(true)
    save({ goalId, timeBudget: next }).catch(() => {
      setLocal(prev)
      toast.error(t('common.error'))
    })
  }

  function rebalance() {
    gate(() => {
      replan({ goalId, rebalance: true }).then(
        () => {
          setChanged(false)
          toast.success(t('timeBudget.rebalancing'))
        },
        (err) => {
          if (!handleUsageLimit(err)) toast.error(t('common.error'))
        },
      )
    })
  }

  const current = choiceForBudget(local)
  return (
    <section className="flex flex-col gap-1.5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full cursor-pointer items-center gap-2 rounded-xl border border-border/70 px-3 py-2 text-left text-[12.5px] transition-colors hover:bg-muted/40"
      >
        <Clock className="size-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">{t('timeBudget.perWeekLabel')}</span>
        <span className="ml-auto font-semibold">{timeBudgetLabel(t, local)}</span>
        <ChevronDown
          className={cn(
            'size-3.5 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5">
          {TIME_BUDGET_CHOICES.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={current === c}
              onClick={() => pick(c)}
              className={cn(
                'cursor-pointer rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                current === c
                  ? 'border-primary/60 bg-primary/10 text-foreground'
                  : 'border-border/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`timeBudget.choice.${c}`)}
            </button>
          ))}
        </div>
      )}
      {changed && (
        <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2">
          <p className="min-w-0 flex-1 text-xs text-muted-foreground">
            {t('timeBudget.rebalanceHint')}
          </p>
          <button
            type="button"
            disabled={replanPending}
            onClick={rebalance}
            className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground transition-opacity disabled:opacity-50"
          >
            <RefreshCw className={cn('size-3', replanPending && 'animate-spin')} />
            {t('timeBudget.rebalance')}
          </button>
        </div>
      )}
    </section>
  )
}
